<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: student.full_name,
                        href: meta.links.student_show,
                    },
                    {
                        name: $t('messages.other_contacts'),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            <FormInput
                                required
                                id="first_name"
                                v-model="form.first_name"
                                name="first_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.first_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6"></div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            <FormInput
                                required
                                id="last_name"
                                v-model="form.last_name"
                                name="last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.last_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            <FormInput
                                id="second_last_name"
                                v-model="form.second_last_name"
                                name="second_last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.second_last_name" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>

                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6"></div>

                    <div class="sm:col-span-6">
                        <label for="gender" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.gender') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <select
                                id="gender"
                                name="gender"
                                v-model="form.gender"
                                required
                                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="">
                                    {{ $t('messages.select') }}
                                </option>
                                <option value="male">
                                    {{ $t('messages.male') }}
                                </option>
                                <option value="female">
                                    {{ $t('messages.female') }}
                                </option>
                                <option value="other">
                                    {{ $t('messages.other') }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nationality')" for="nationality" />
                            <FormSelectFilterablePanel
                                id="nationality"
                                required
                                v-model="form.nationality"
                                :options="options.countriesByInitial"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.nationality" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.identification_document')" for="identification_document" />
                            <FormInput
                                id="identification_document"
                                v-model="form.identification_document"
                                name="identification_document"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.identification_document" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            <FormInput
                                id="email"
                                v-model="form.email"
                                name="email"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.email" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.phones', 2) }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6" v-for="(phone, index) in form.phones" :key="index">
                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.phones', 1) }}
                        </label>
                        <div class="mt-1 flex rounded-md shadow-sm">
                            <span
                                class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                            >
                                <PhonePrefixSelector v-model="phone.dial_code"></PhonePrefixSelector>
                            </span>
                            <input
                                type="tel"
                                :name="`phone.${index}`"
                                :title="$t('messages.only_numbers')"
                                v-model="phone.number"
                                required
                                pattern="[0-9]+"
                                class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300"
                            />
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.label') }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="text"
                                v-model="phone.label"
                                list="phone_labels"
                                class="w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="mt-1">
                            <button
                                type="button"
                                @click="deletePhone(index)"
                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col mt-5">
                    <div>
                        <button
                            type="button"
                            @click="addPhone()"
                            class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                class="h-5 w-5"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.other') }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.relationship')" for="relationship" />
                            <FormSelect
                                id="relationship"
                                required
                                v-model="form.relationship"
                                :options="options.relationshipTypes"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.relationship" class="mt-2" />
                        </div>
                    </div>

                    <div class="md:col-span-2">
                        <label for="can_take_student" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.can_take_student') }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="checkbox"
                                id="can_take_student"
                                name="can_take_student"
                                v-model="form.can_take_student"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <datalist id="phone_labels">
                <option :value="$t('messages.home_phone')"></option>
                <option :value="$t('messages.work_phone')"></option>
                <option :value="$t('messages.mobile_phone')"></option>
                <option :value="$t('messages.other')"></option>
            </datalist>

            <div>
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import PhonePrefixSelector from '@/components/pageSpecific/PhonePrefixSelector.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormSelectFilterablePanel from '@/components/Forms/FormSelectFilterablePanel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        PhonePrefixSelector,
        FormLabel,
        FormInput,
        FormError,
        FormSelect,
        FormSelectFilterablePanel,
    },
    layout: Layout,
    name: 'Create',
    props: ['student', 'options', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                first_name: '',
                last_name: '',
                second_last_name: '',
                gender: '',
                nationality: 'ES',
                email: '',
                relationship: '',
                identification_document: '',
                can_take_student: false,
                phones: [],
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        addPhone() {
            this.form.phones.push({
                dial_code: '34', //Spain
                number: '',
                label: '',
            });
        },
        deletePhone(index) {
            this.form.phones.splice(index, 1);
        },
    },
};
</script>
